/**
 * Impulsionar — cria campanha + conjunto + criativo + anúncio na Meta em
 * cinco etapas, tudo pausado, com prévia do anúncio ao lado e um assistente
 * de IA que propõe texto, público e verba.
 *
 * Estrutura do BroadcastStepper: acordeão de etapas com resumo quando
 * fechada, barra fixa no topo com as ações. A criação é UMA mutation no
 * servidor (que grava o id de cada passo); se parar no meio, o diálogo de
 * progresso mostra o que existe e deixa retomar. Rota `/ads/new`.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  Search,
  Sparkles,
  Target,
  Upload,
  Users,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { OBJETIVOS_IMPULSIONAR, OBJETIVO_KEYS, IDADE_MAX, IDADE_MIN, type ObjetivoImpulsionar } from "@shared/meta-ads-boost";
import { ABORDAGENS_COPY, type AbordagemCopy } from "@shared/copy-frameworks";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { centavosParaReais, moeda, reaisParaCentavos } from "@/lib/meta-ads-format";
import {
  FORM_INICIAL,
  STEP_KEYS,
  formParaInput,
  isStepComplete,
  nomeAutomatico,
  pendenciaDaEtapa,
  resumoDaEtapa,
  type BoostForm,
  type ContextoValidacao,
  type MediaEscolhida,
  type StepKey,
} from "@/lib/meta-ads-boost-form";
import { TargetingPicker } from "@/components/ads/boost/TargetingPicker";
import { PreviewAnuncio } from "@/components/ads/boost/PreviewAnuncio";
import { ProgressoCriacao, type Passos } from "@/components/ads/boost/ProgressoCriacao";
import { ConfirmarAtivacaoDialog } from "@/components/ads/boost/ConfirmarAtivacaoDialog";

const STEPS: Array<{ key: StepKey; icon: React.ReactNode; title: string; description: string }> = [
  { key: "conteudo", icon: <ImageIcon className="h-4 w-4" />, title: "Conteúdo", description: "O post que vai virar anúncio, ou um anúncio novo." },
  { key: "objetivo", icon: <Target className="h-4 w-4" />, title: "Objetivo", description: "O que você quer que a pessoa faça." },
  { key: "publico", icon: <Users className="h-4 w-4" />, title: "Público", description: "Quem vai ver." },
  { key: "verba", icon: <Wallet className="h-4 w-4" />, title: "Verba e período", description: "Quanto por dia e por quanto tempo." },
  { key: "revisao", icon: <ClipboardCheck className="h-4 w-4" />, title: "Revisão", description: "Confira antes de criar." },
];

// ─── Etapas ──────────────────────────────────────────────────────────────────

function PostGrid({
  selecionado,
  onSelect,
  accountId,
}: {
  selecionado: string | null;
  onSelect: (m: MediaEscolhida) => void;
  /** Conta do Instagram da Página que vai anunciar. */
  accountId?: number;
}) {
  const utils = trpc.useUtils();
  const [busca, setBusca] = useState("");
  const posts = trpc.metaAds.boost.instagramMedia.useQuery({ limit: 24, accountId }, { staleTime: 60_000 });
  const [extras, setExtras] = useState<NonNullable<typeof posts.data>["items"]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [carregando, setCarregando] = useState(false);
  const proximo = cursor === undefined ? posts.data?.nextCursor ?? null : cursor;
  const carregarMais = async () => {
    if (!proximo) return;
    setCarregando(true);
    try {
      const r = await utils.metaAds.boost.instagramMedia.fetch({ limit: 24, after: proximo, accountId });
      setExtras((e) => [...e, ...r.items]);
      setCursor(r.nextCursor);
    } catch (err) {
      toast.error(toUserFriendlyError(err));
    } finally {
      setCarregando(false);
    }
  };
  const todos = useMemo(() => [...(posts.data?.items ?? []), ...extras].filter((m) => m.mediaProductType !== "STORY"), [posts.data, extras]);
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return q ? todos.filter((m) => (m.caption ?? "").toLowerCase().includes(q)) : todos;
  }, [todos, busca]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar pela legenda…" className="pl-9" />
      </div>
      {posts.isLoading && <p className="text-xs text-muted-foreground">Carregando posts…</p>}
      {posts.isError && <p className="text-xs text-red-600">{toUserFriendlyError(posts.error)}</p>}
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
        {filtrados.map((m) => {
          const thumb = m.thumbnailUrl ?? (m.mediaType === "VIDEO" ? null : m.mediaUrl);
          const sel = selecionado === m.id;
          return (
            <button
              key={m.id}
              type="button"
              title={m.caption ?? ""}
              onClick={() => onSelect({ id: m.id, caption: m.caption, permalink: m.permalink, thumbUrl: thumb, mediaProductType: m.mediaProductType })}
              className={`relative aspect-square overflow-hidden rounded-md border bg-muted transition ${sel ? "ring-2 ring-primary ring-offset-2" : "hover:opacity-90"}`}
            >
              {thumb ? <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" /> : <span className="flex h-full items-center justify-center text-[10px] text-muted-foreground">{m.mediaType}</span>}
              {m.mediaProductType === "REELS" && <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[9px] text-white">Reel</span>}
              {sel && <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-white"><Check className="h-3 w-3" /></span>}
            </button>
          );
        })}
      </div>
      {proximo && (
        <Button type="button" size="sm" variant="outline" onClick={() => void carregarMais()} disabled={carregando}>
          {carregando && <Loader2 className="mr-2 h-3 w-3 animate-spin" />} Carregar mais posts
        </Button>
      )}
    </div>
  );
}

function StepConteudo({
  form,
  set,
  instagramConectado,
  instagramAccountId,
}: {
  form: BoostForm;
  set: <K extends keyof BoostForm>(k: K, v: BoostForm[K]) => void;
  instagramConectado: boolean;
  instagramAccountId?: number;
}) {
  const upload = trpc.metaAds.creatives.uploadImage.useMutation({
    onSuccess: (r) => {
      set("linkAd", { ...form.linkAd, imageHash: r.hash, imageUrl: r.url ?? form.linkAd.imageUrl });
      toast.success("Imagem enviada para a conta de anúncios.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  return (
    <Tabs value={form.conteudoTipo} onValueChange={(v) => set("conteudoTipo", v as BoostForm["conteudoTipo"])}>
      <TabsList>
        <TabsTrigger value="instagram_post">Post do Instagram</TabsTrigger>
        <TabsTrigger value="link">Anúncio novo (imagem + texto + link)</TabsTrigger>
      </TabsList>
      <TabsContent value="instagram_post" className="pt-3">
        {instagramConectado ? (
          <PostGrid
            key={instagramAccountId ?? "unica"}
            selecionado={form.media?.id ?? null}
            onSelect={(m) => set("media", m)}
            accountId={instagramAccountId}
          />
        ) : (
          <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            O Instagram não está conectado. Conecte em Configurações → Integrações → Instagram, ou crie um anúncio novo na outra aba.
          </p>
        )}
        {form.media?.permalink && (
          <a href={form.media.permalink} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline">
            <ExternalLink className="h-3 w-3" /> Ver post
          </a>
        )}
      </TabsContent>
      <TabsContent value="link" className="space-y-3 pt-3">
        <div className="space-y-1.5">
          <Label>Texto do anúncio</Label>
          <Textarea rows={4} value={form.linkAd.message} onChange={(e) => set("linkAd", { ...form.linkAd, message: e.target.value })} maxLength={5000} placeholder="O que a pessoa lê acima da imagem." />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Título (opcional)</Label>
            <Input value={form.linkAd.headline} onChange={(e) => set("linkAd", { ...form.linkAd, headline: e.target.value })} maxLength={200} />
          </div>
          <div className="space-y-1.5">
            <Label>Link de destino</Label>
            <Input value={form.linkAd.link} onChange={(e) => set("linkAd", { ...form.linkAd, link: e.target.value })} placeholder="https://…" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Imagem (URL pública)</Label>
          <div className="flex gap-2">
            <Input value={form.linkAd.imageUrl} onChange={(e) => set("linkAd", { ...form.linkAd, imageUrl: e.target.value, imageHash: null })} placeholder="https://…/imagem.jpg" />
            <Button type="button" variant="outline" onClick={() => upload.mutate({ imageUrl: form.linkAd.imageUrl })} disabled={!/^https?:\/\//.test(form.linkAd.imageUrl) || upload.isPending}>
              {upload.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {form.linkAd.imageHash ? "Enviada ✓" : "Enviar"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">A Meta baixa a imagem do endereço e guarda na conta de anúncios. Formato 1:1 ou 4:5, mínimo 1080 px.</p>
        </div>
      </TabsContent>
    </Tabs>
  );
}

function StepObjetivo({ form, set, whatsappOk, whatsappNumero }: { form: BoostForm; set: <K extends keyof BoostForm>(k: K, v: BoostForm[K]) => void; whatsappOk: boolean | null; whatsappNumero: string | null }) {
  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2">
        {OBJETIVO_KEYS.map((k) => {
          const d = OBJETIVOS_IMPULSIONAR[k];
          const ativo = form.objetivo === k;
          const bloqueado = k === "whatsapp" && whatsappOk === false;
          return (
            <button
              key={k}
              type="button"
              disabled={bloqueado}
              onClick={() => set("objetivo", k)}
              className={`rounded-lg border p-3 text-left transition ${ativo ? "border-primary bg-primary/5" : "hover:border-muted-foreground/40"} ${bloqueado ? "opacity-60" : ""}`}
            >
              <p className="text-sm font-medium">{d.label}</p>
              <p className="text-xs text-muted-foreground">{d.descricao}</p>
              {k === "whatsapp" && whatsappOk === false && <p className="mt-1 text-xs text-amber-700">A Página não tem WhatsApp vinculado. Vincule o número oficial na Página do Facebook.</p>}
              {k === "whatsapp" && whatsappNumero && <p className="mt-1 text-xs text-muted-foreground">Número da Página: {whatsappNumero}</p>}
            </button>
          );
        })}
      </div>
      {form.objetivo === "whatsapp" && (
        <p className="rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
          Para o LeadConversa registrar de qual anúncio cada conversa veio, o número da Página precisa ser o mesmo conectado ao WhatsApp aqui.
        </p>
      )}
      {form.objetivo && OBJETIVOS_IMPULSIONAR[form.objetivo].precisaLink && (
        <div className="space-y-1.5">
          <Label>Link para onde o anúncio leva</Label>
          <Input value={form.link} onChange={(e) => set("link", e.target.value)} placeholder={form.conteudoTipo === "link" && form.linkAd.link ? form.linkAd.link : "https://…"} />
        </div>
      )}
    </div>
  );
}

function StepPublico({ form, set }: { form: BoostForm; set: <K extends keyof BoostForm>(k: K, v: BoostForm[K]) => void }) {
  const p = form.publico;
  const setP = (patch: Partial<typeof p>) => set("publico", { ...p, ...patch });
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>Onde</Label>
        <TargetingPicker tipo="adgeolocation" selecionados={p.locais} onChange={(locais) => setP({ locais })} placeholder="Cidade, estado ou país…" />
      </div>
      <div className="space-y-1.5">
        <Label>
          Idade: {p.idadeMin} – {p.idadeMax === IDADE_MAX ? "65+" : p.idadeMax}
        </Label>
        <Slider min={IDADE_MIN} max={IDADE_MAX} step={1} value={[p.idadeMin, p.idadeMax]} onValueChange={([a, b]) => setP({ idadeMin: Math.min(a!, b!), idadeMax: Math.max(a!, b!) })} />
      </div>
      <div className="space-y-1.5">
        <Label>Gênero</Label>
        <ToggleGroup type="single" value={p.genero} onValueChange={(v) => v && setP({ genero: v as typeof p.genero })} variant="outline">
          <ToggleGroupItem value="all">Todos</ToggleGroupItem>
          <ToggleGroupItem value="female">Mulheres</ToggleGroupItem>
          <ToggleGroupItem value="male">Homens</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div className="space-y-1.5">
        <Label>Interesses (opcional)</Label>
        <TargetingPicker tipo="adinterest" selecionados={p.interesses.map((i) => ({ key: i.id, name: i.name, type: "interest" }))} onChange={(itens) => setP({ interesses: itens.map((i) => ({ id: i.key, name: i.name })) })} placeholder="Ex.: Licitação, Administração pública…" />
      </div>
      <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
        <div>
          <p className="text-sm font-medium">Público Advantage+</p>
          <p className="text-xs text-muted-foreground">A Meta amplia o público além dos filtros quando encontra gente com maior chance de responder. Recomendado.</p>
        </div>
        <Switch checked={p.advantagePlus} onCheckedChange={(v) => setP({ advantagePlus: v })} />
      </div>
      <p className="text-xs text-muted-foreground">Posicionamentos automáticos (Facebook e Instagram).</p>
    </div>
  );
}

function StepVerba({ form, set, capCents, minCents, currency, timezoneName }: { form: BoostForm; set: <K extends keyof BoostForm>(k: K, v: BoostForm[K]) => void; capCents: number | null; minCents: number; currency: string | null; timezoneName: string | null }) {
  const v = form.verba;
  const setV = (patch: Partial<typeof v>) => set("verba", { ...v, ...patch });
  const cents = reaisParaCentavos(v.dailyTexto);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Verba por dia ({currency ?? "BRL"})</Label>
          <Input inputMode="decimal" value={v.dailyTexto} onChange={(e) => setV({ dailyTexto: e.target.value })} placeholder="50,00" />
          <p className="text-xs text-muted-foreground">
            Mínimo {moeda(minCents, currency)}. {capCents != null ? `Limite do workspace: ${moeda(capCents, currency)}/dia.` : "Sem limite definido."}
          </p>
          {cents != null && capCents != null && cents > capCents && <p className="text-xs text-red-600">Acima do limite.</p>}
        </div>
        <div className="space-y-1.5">
          <Label>Início</Label>
          <ToggleGroup type="single" value={v.inicio} onValueChange={(x) => x && setV({ inicio: x as typeof v.inicio })} variant="outline">
            <ToggleGroupItem value="now">Assim que ativar</ToggleGroupItem>
            <ToggleGroupItem value="date">Em uma data</ToggleGroupItem>
          </ToggleGroup>
          {v.inicio === "date" && (
            <div className="flex gap-2">
              <Input type="date" value={v.dataInicio} onChange={(e) => setV({ dataInicio: e.target.value })} />
              <Input type="time" value={v.horaInicio} onChange={(e) => setV({ horaInicio: e.target.value })} className="w-28" />
            </div>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Término (opcional)</Label>
        <Input type="date" value={v.dataFim} onChange={(e) => setV({ dataFim: e.target.value })} className="sm:w-56" />
        <p className="text-xs text-muted-foreground">Sem término, cobra todo dia até você pausar.{timezoneName ? ` As datas seguem o fuso da conta (${timezoneName}).` : ""}</p>
      </div>
    </div>
  );
}

function Linha({ ok, label, valor }: { ok: boolean; label: string; valor: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
      <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1">{valor}</span>
    </div>
  );
}

// ─── Assistente de IA ────────────────────────────────────────────────────────

function AssistenteIA({ form, aplicar }: { form: BoostForm; aplicar: (s: NonNullable<ReturnType<typeof useSugestao>["data"]>) => void }) {
  const [briefing, setBriefing] = useState("");
  const [abordagem, setAbordagem] = useState<AbordagemCopy>("livre");
  const [aberto, setAberto] = useState(false);
  const sugerir = useSugestao();
  const legenda = form.conteudoTipo === "instagram_post" ? form.media?.caption ?? null : form.linkAd.message || null;
  const pode = !!form.objetivo;
  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-primary" /> Assistente de tráfego</p>
            <p className="text-xs text-muted-foreground">Propõe público, verba{form.conteudoTipo === "link" ? ", texto" : ""} e nome a partir do post e do objetivo. Você revisa antes de criar.</p>
          </div>
          <Button size="sm" variant={aberto ? "ghost" : "default"} onClick={() => setAberto((v) => !v)} disabled={!pode} title={pode ? "" : "Escolha o objetivo primeiro"}>
            {aberto ? "Fechar" : "Sugerir"}
          </Button>
        </div>
        {aberto && (
          <div className="space-y-2">
            <Textarea rows={2} value={briefing} onChange={(e) => setBriefing(e.target.value)} placeholder="Opcional: contexto (ex.: curso presencial em Campo Grande para servidores públicos; público 30–55)." maxLength={2000} />
            {form.conteudoTipo === "link" && (
              <div className="space-y-1">
                <Label className="text-xs">Estrutura do texto</Label>
                <Select value={abordagem} onValueChange={(v) => setAbordagem(v as AbordagemCopy)}>
                  <SelectTrigger className="h-9 max-w-md"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(ABORDAGENS_COPY) as AbordagemCopy[]).map((k) => (
                      <SelectItem key={k} value={k}>{ABORDAGENS_COPY[k].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{ABORDAGENS_COPY[abordagem].descricao}</p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => sugerir.mutate({ objectiveKey: form.objetivo!, legenda, link: form.link || form.linkAd.link || null, briefing: briefing || null, precisaTexto: form.conteudoTipo === "link", abordagem })}
                disabled={sugerir.isPending}
              >
                {sugerir.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Gerar proposta
              </Button>
              {sugerir.isError && <span className="text-xs text-red-600">{toUserFriendlyError(sugerir.error)}</span>}
            </div>
            {sugerir.data && (
              <div className="space-y-2 rounded border bg-background p-3 text-sm">
                <p className="text-xs text-muted-foreground">{sugerir.data.justificativa}</p>
                <p><span className="text-muted-foreground">Nome:</span> {sugerir.data.nome}</p>
                {sugerir.data.mensagem && <p><span className="text-muted-foreground">Texto:</span> {sugerir.data.mensagem}</p>}
                <p><span className="text-muted-foreground">Onde:</span> {sugerir.data.publico.locais.map((l) => l.name).join(", ")}</p>
                <p><span className="text-muted-foreground">Interesses:</span> {sugerir.data.publico.interesses.map((i) => i.name).join(", ") || "—"}</p>
                <p><span className="text-muted-foreground">Idade/gênero:</span> {sugerir.data.publico.idadeMin}–{sugerir.data.publico.idadeMax === 65 ? "65+" : sugerir.data.publico.idadeMax} · {sugerir.data.publico.genero === "all" ? "todos" : sugerir.data.publico.genero === "male" ? "homens" : "mulheres"}</p>
                <p><span className="text-muted-foreground">Verba:</span> {moeda(sugerir.data.verbaDiariaCents, null)}/dia</p>
                {sugerir.data.publico.naoEncontrados.length > 0 && <p className="text-xs text-amber-700">Não encontrei na Meta: {sugerir.data.publico.naoEncontrados.join(", ")}.</p>}
                <Button size="sm" onClick={() => { aplicar(sugerir.data!); setAberto(false); toast.success("Proposta aplicada — revise as etapas."); }}>
                  <Check className="mr-2 h-4 w-4" /> Aplicar ao formulário
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function useSugestao() {
  return trpc.metaAds.boost.suggest.useMutation();
}

// ─── Página ──────────────────────────────────────────────────────────────────

function AdsBoostContent() {
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const canActivate = can("ads:activate");

  const contexto = trpc.metaAds.boost.context.useQuery(undefined, { staleTime: 5 * 60_000, retry: false });
  const [form, setForm] = useState<BoostForm>(FORM_INICIAL);
  const [aberta, setAberta] = useState<StepKey>("conteudo");
  const [progresso, setProgresso] = useState<{ open: boolean; rodando: boolean; passos: Passos | null; erro: { passo: string; message: string } | null; boostId: number | null; campaignId: string | null; ativarDepois: boolean }>({ open: false, rodando: false, passos: null, erro: null, boostId: null, campaignId: null, ativarDepois: false });
  const [confirmarAtivacao, setConfirmarAtivacao] = useState(false);

  const set = <K extends keyof BoostForm>(k: K, v: BoostForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Nome automático enquanto a pessoa não editou.
  useEffect(() => {
    if (!form.nomeEditado) setForm((f) => ({ ...f, nome: nomeAutomatico(f) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.media?.id, form.linkAd.message, form.conteudoTipo, form.nomeEditado]);

  const ctxVal: ContextoValidacao = {
    capCents: contexto.data?.budgetCapCents ?? null,
    minCents: contexto.data?.minDailyBudgetCents ?? 100,
    instagramConectado: contexto.data?.instagramConectado ?? false,
    whatsappOk: contexto.data ? (contexto.data.whatsappNumero ? true : null) : null,
  };
  const tudoPronto = STEP_KEYS.every((s) => isStepComplete(s, form, ctxVal));
  const pagina = contexto.data?.paginaSugerida ?? null;
  const igUserId = pagina?.instagram?.id ?? contexto.data?.igUserId ?? null;
  // Os posts listados são os do Instagram da Página que vai anunciar. Com
  // várias contas conectadas, sem isso o servidor não saberia de qual.
  const instagramAccountId =
    contexto.data?.instagramContas.find((c) => c.igUserId === igUserId)?.id ??
    contexto.data?.instagramContas.find((c) => c.pageId === pagina?.id)?.id;
  const currency = contexto.data?.account.currency ?? null;
  const contaBloqueada = contexto.data?.account.accountStatus != null && ![1, 9].includes(contexto.data.account.accountStatus);

  const criar = trpc.metaAds.boost.create.useMutation();
  const retomar = trpc.metaAds.boost.resume.useMutation();
  const ativar = trpc.metaAds.boost.activate.useMutation();

  const tratarResultado = (r: Awaited<ReturnType<typeof criar.mutateAsync>>, ativarDepois: boolean) => {
    setProgresso({ open: true, rodando: false, passos: r.passos, erro: r.erro, boostId: r.boostId, campaignId: r.campaignId, ativarDepois });
    void utils.metaAds.boost.list.invalidate();
    void utils.metaAds.campaigns.list.invalidate();
    if (!r.erro && ativarDepois) {
      setProgresso((p) => ({ ...p, open: false }));
      setConfirmarAtivacao(true);
    }
  };

  const executar = async (ativarDepois: boolean) => {
    if (!pagina) return toast.error("Nenhuma Página do Facebook disponível na credencial de Ads.");
    setProgresso({ open: true, rodando: true, passos: null, erro: null, boostId: null, campaignId: null, ativarDepois });
    try {
      const r = await criar.mutateAsync(formParaInput(form, { pageId: pagina.id, igUserId, adAccountId: contexto.data?.account.accountId }));
      tratarResultado(r, ativarDepois);
    } catch (err) {
      setProgresso((p) => ({ ...p, rodando: false, erro: { passo: "validação", message: toUserFriendlyError(err) } }));
    }
  };
  const tentarDeNovo = async () => {
    if (!progresso.boostId) return void executar(progresso.ativarDepois);
    setProgresso((p) => ({ ...p, rodando: true, erro: null }));
    try {
      const r = await retomar.mutateAsync({ boostId: progresso.boostId });
      tratarResultado(r, progresso.ativarDepois);
    } catch (err) {
      setProgresso((p) => ({ ...p, rodando: false, erro: { passo: "retomada", message: toUserFriendlyError(err) } }));
    }
  };
  const confirmarAtivar = async () => {
    if (!progresso.boostId) return;
    try {
      await ativar.mutateAsync({ boostId: progresso.boostId });
      toast.success("No ar! A Meta pode levar alguns minutos para começar a veicular.");
      setConfirmarAtivacao(false);
      navigate(progresso.campaignId ? `/ads/campaigns/${progresso.campaignId}` : "/ads");
    } catch (err) {
      toast.error(toUserFriendlyError(err));
    }
  };

  const aplicarSugestao = (s: NonNullable<ReturnType<typeof useSugestao>["data"]>) => {
    setForm((f) => ({
      ...f,
      nome: s.nome,
      nomeEditado: true,
      linkAd: s.mensagem ? { ...f.linkAd, message: s.mensagem, headline: s.titulo ?? f.linkAd.headline } : f.linkAd,
      publico: { ...f.publico, locais: s.publico.locais, interesses: s.publico.interesses, idadeMin: s.publico.idadeMin, idadeMax: s.publico.idadeMax, genero: s.publico.genero },
      verba: { ...f.verba, dailyTexto: centavosParaReais(s.verbaDiariaCents) },
    }));
    setAberta("publico");
  };

  if (contexto.isLoading) return <p className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</p>;
  if (contexto.isError) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-red-600">{toUserFriendlyError(contexto.error)}</p>
        <Button variant="link" onClick={() => navigate("/ads")}>Voltar para Anúncios</Button>
      </div>
    );
  }

  const inicioParaEstimativa = form.verba.inicio === "date" && form.verba.dataInicio ? new Date(`${form.verba.dataInicio}T${form.verba.horaInicio || "09:00"}:00`) : new Date();
  const fimParaEstimativa = form.verba.dataFim ? new Date(`${form.verba.dataFim}T23:59:00`) : null;

  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-10 -mx-4 flex flex-wrap items-center gap-3 border-b bg-background/95 px-4 py-3 backdrop-blur">
        <Button variant="ghost" size="sm" onClick={() => navigate("/ads")}><ArrowLeft className="mr-1 h-4 w-4" /> Anúncios</Button>
        <Input value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value, nomeEditado: true }))} placeholder="Nome do impulsionamento" className="h-9 max-w-md flex-1 font-medium" maxLength={200} />
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => void executar(false)} disabled={!tudoPronto || contaBloqueada || criar.isPending}>Criar pausado</Button>
          {canActivate && <Button onClick={() => void executar(true)} disabled={!tudoPronto || contaBloqueada || criar.isPending}>Criar e ativar</Button>}
        </div>
      </div>

      {contaBloqueada && <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">A conta de anúncios não está ativa na Meta — dá para montar, mas a criação vai ser recusada até resolver no Gerenciador de Negócios.</p>}
      {pagina && <p className="text-xs text-muted-foreground">Página: <strong>{pagina.name}</strong>{pagina.instagram ? ` · Instagram @${pagina.instagram.username}` : " · sem Instagram vinculado"} · conta {contexto.data?.account.accountId}</p>}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-3">
          <AssistenteIA form={form} aplicar={aplicarSugestao} />

          {STEPS.map((s, i) => {
            const completa = isStepComplete(s.key, form, ctxVal);
            const pend = pendenciaDaEtapa(s.key, form, ctxVal);
            const expandida = aberta === s.key;
            return (
              <Card key={s.key}>
                <button type="button" className="flex w-full items-center gap-3 p-4 text-left" onClick={() => setAberta(expandida ? ("" as StepKey) : s.key)}>
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${completa ? "bg-emerald-600 text-white" : "bg-primary/10 text-primary"}`}>
                    {completa ? <Check className="h-4 w-4" /> : i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 text-sm font-medium">{s.icon} {s.title} {completa && <Badge variant="secondary" className="text-[10px]">Completo</Badge>}</span>
                    <span className="block truncate text-xs text-muted-foreground">{expandida ? s.description : completa ? resumoDaEtapa(s.key, form) : pend}</span>
                  </span>
                  {expandida ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </button>
                {expandida && (
                  <CardContent className="pt-0">
                    <Separator className="mb-4" />
                    {s.key === "conteudo" && <StepConteudo form={form} set={set} instagramConectado={ctxVal.instagramConectado} instagramAccountId={instagramAccountId} />}
                    {s.key === "objetivo" && <StepObjetivo form={form} set={set} whatsappOk={ctxVal.whatsappOk} whatsappNumero={contexto.data?.whatsappNumero ?? null} />}
                    {s.key === "publico" && <StepPublico form={form} set={set} />}
                    {s.key === "verba" && <StepVerba form={form} set={set} capCents={ctxVal.capCents} minCents={ctxVal.minCents} currency={currency} timezoneName={contexto.data?.account.timezoneName ?? null} />}
                    {s.key === "revisao" && (
                      <div className="space-y-2">
                        <Linha ok={isStepComplete("conteudo", form, ctxVal)} label="Conteúdo" valor={resumoDaEtapa("conteudo", form)} />
                        <Linha ok={isStepComplete("objetivo", form, ctxVal)} label="Objetivo" valor={resumoDaEtapa("objetivo", form)} />
                        <Linha ok={isStepComplete("publico", form, ctxVal)} label="Público" valor={resumoDaEtapa("publico", form)} />
                        <Linha ok={isStepComplete("verba", form, ctxVal)} label="Verba" valor={resumoDaEtapa("verba", form)} />
                        <Linha ok={!!form.nome.trim()} label="Nome" valor={form.nome || "—"} />
                        <p className="pt-2 text-xs text-muted-foreground">Tudo é criado <strong>pausado</strong>. Nada é cobrado até você ativar.</p>
                      </div>
                    )}
                    {s.key !== "revisao" && (
                      <div className="mt-4 flex justify-end">
                        <Button size="sm" variant="outline" onClick={() => setAberta(STEPS[i + 1]!.key)}>Próximo</Button>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
        <div className="lg:sticky lg:top-20 lg:self-start">
          <PreviewAnuncio form={form} username={pagina?.instagram?.username ?? contexto.data?.igUsername ?? null} />
        </div>
      </div>

      <ProgressoCriacao
        open={progresso.open}
        rodando={progresso.rodando}
        passos={progresso.passos}
        erro={progresso.erro}
        campaignId={progresso.campaignId}
        adAccountId={contexto.data?.account.accountId ?? null}
        onRetry={() => void tentarDeNovo()}
        onClose={() => {
          setProgresso((p) => ({ ...p, open: false }));
          navigate(progresso.campaignId ? `/ads/campaigns/${progresso.campaignId}` : "/ads");
        }}
      />
      <ConfirmarAtivacaoDialog
        open={confirmarAtivacao}
        onOpenChange={(o) => {
          setConfirmarAtivacao(o);
          if (!o && !ativar.isPending) navigate(progresso.campaignId ? `/ads/campaigns/${progresso.campaignId}` : "/ads");
        }}
        dailyCents={reaisParaCentavos(form.verba.dailyTexto) ?? 0}
        currency={currency}
        capCents={ctxVal.capCents}
        inicio={inicioParaEstimativa}
        fim={fimParaEstimativa}
        onConfirm={() => void confirmarAtivar()}
        isPending={ativar.isPending}
      />
    </div>
  );
}

export default function AdsBoost() {
  return (
    <PermissionGuard permission="ads:manage">
      <DashboardLayout>
        <AdsBoostContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
